import React, { createContext, useEffect, useRef, useState } from "react";
import OrderSelectionList from "../OrderSelectionList";
import DocumentUploadPage from "../DocumentUploadPage";
import BasicClaimInfoForm from "../BasicClaimInfoForm";
import { Stepper } from "primereact/stepper";
import { StepperPanel } from "primereact/stepperpanel";
import { Toast } from "primereact/toast";
import "primereact/resources/themes/lara-light-cyan/theme.css";
import "./CreateNewClaimModal.css";
import FullClaimInfoForm from "../FullClaimInfoForm";
import {
  createPacket,
  updatePacket,
  createPayoffQuote,
  createOrderRequest,
  createLoGRequest,
  createOrderGroupRequest,
  fetchPacket
} from "../../services/packets";
import { getVendorConnectedCompanies } from "../../services/vendors";
import { getAccountInfo } from "../../services/users";
import {
  requirementsMatrix,
  allOrderTypes,
  salvageBrands
} from "../../services/constants";
import { buildAddressStringFromObject } from "../../services/formatting";
import { fetchCountriesAndStates } from "../../services/local-storage";

export const SidebarContext = createContext();

const CreateNewClaimModal = ({
  workingPacket,
  refreshPacket,
  mode = "create",
  changeMode,
  revertMode
}) => {
  const [carrier, setCarrier] = useState([]);
  const [lender, setLender] = useState({});
  const [skipRequiredDocs, setSkipRequiredDocs] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [user, setUser] = useState({});
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [existingOrders, setExistingOrders] = useState([]);
  const [xTitleSelected, setXTitleSelected] = useState(false);
  const [xTitleChecked, setXTitleChecked] = useState(false);
  const [completedOrderTypes, setCompletedOrderTypes] = useState([]);
  const [titleTypes, setTitleTypes] = useState({});
  const [basicRequiredFields, setBasicRequiredFields] = useState([]);
  const [fullRequiredFields, setFullRequiredFields] = useState([]);
  const [optionalFields, setOptionalFields] = useState([]);
  const [basicClaimInfo, setBasicClaimInfo] = useState({});
  const [titleState, setTitleState] = useState(null);
  const [titleType, setTitleType] = useState(null);
  const [packet, setPacket] = useState(null);
  const [uploadedDocs, setUploadedDocs] = useState([]);
  const [existingPacket, setExistingPacket] = useState(workingPacket);
  const [fullClaimInfo, setFullClaimInfo] = useState({});
  const [financed, setFinanced] = useState(false);
  const [ownerSignedTitleImage, setOwnerSignedTitleImage] = useState(true);
  const [odometerInTitleImage, setOdometerInTitleImage] = useState(true);
  const [signedLRInTitleImage, setSignedLRInTitleImage] = useState(true);
  const [states, setStates] = useState([]);
  const stepperRef = useRef(null);
  const toastRef = useRef(null);

  // modes
  // create - create a new claim, include steps 1-4
  // orders - select orders only, include step 2, 4
  // documents - upload documents only, include step 3
  // newxTitle - create a new xTitle, include steps 2, 3, 4

  useEffect(() => {
    if (existingPacket) {
      setFinanced(existingPacket.financeType !== "Owned");
    }
  }, [existingPacket]);

  useEffect(() => {
    const fetchData = async () => {
      const data = await fetchCountriesAndStates();
      const US = data?.countries?.find(
        country => country.countryName === "United States"
      );
      const USStates = data?.states?.filter(
        state => state.countryId === US.countryId
      );
      setStates(USStates);
    };
    if (!states || states?.length === 0) {
      fetchData();
    }
  }, [states, selectedOrders]);

  const testing = window.location.hostname === "localhost";

  // sets xTitleSelected to true if xTitle currently placed on packet
  // or if xTitle is selected in the form
  useEffect(() => {
    const existingContainsXTitle = existingOrders["xTitle"];
    const selectedContainsXTitle = selectedOrders["xTitle"];
    if ((existingContainsXTitle || selectedContainsXTitle) && !xTitleSelected) {
      setXTitleSelected(true);
    }
    setXTitleSelected(false);
  }, [existingOrders, selectedOrders, xTitleSelected]);

  // sets packet and existing packet when working packet changes
  useEffect(() => {
    if (workingPacket && workingPacket.packetId) {
      workingPacket.accountNumber = workingPacket.accountNumber
        ? "Click to Show"
        : null;
      setExistingPacket(workingPacket);
      setPacket(workingPacket);
    }
  }, [workingPacket]);

  // handles setting required fields based on selected and existing orders
  // as well as setting the selected orders
  useEffect(() => {
    // orders will contain all existing orders as well as any selected orders from the form
    const orders = {};

    // add existing orders
    workingPacket?.orders?.forEach(p => {
      const orderName = allOrderTypes.find(o => {
        if (
          o.label === p.type ||
          (o.label === "Title Image" && p.type === "Copy of Title")
        ) {
          return o.name;
        }
        return null;
      })?.name;
      orders[orderName] = true;
    });
    // add existing payoff
    if (workingPacket?.payoffQuote) {
      orders["loanPayoff"] = true;
    }
    if (Object.keys(existingOrders)?.length !== Object.keys(orders)?.length) {
      setExistingOrders(orders);
    }
    // add selected orders
    Object.keys(selectedOrders).forEach(order => {
      orders[order] = true;
    });

    if (orders["xTitle"]) {
      setXTitleSelected(true);
    }

    const requiredFieldsBasedOnOrders = [
      ...new Set(
        Object.keys(orders)
          .map(order => requirementsMatrix[order]?.requiredData)
          .flat()
      )
    ];

    const b = requirementsMatrix.allBasic;
    const f = requiredFieldsBasedOnOrders.filter(
      field =>
        requirementsMatrix.allFull.includes(field) &&
        !requirementsMatrix.allBasic.includes(field)
    );

    if (basicRequiredFields.length !== b.length) {
      setBasicRequiredFields(b);
    }

    if (fullRequiredFields.length !== f.length) {
      setFullRequiredFields(f);
    }

    const optionalFieldsBasedOnOrders = [
      ...new Set(
        Object.keys(orders)
          .map(order => requirementsMatrix[order]?.optionalData)
          .flat()
      )
    ];
    setOptionalFields(optionalFieldsBasedOnOrders);
  }, [
    selectedOrders,
    setOptionalFields,
    existingOrders,
    workingPacket,
    packet,
    mode,
    setBasicRequiredFields,
    setFullRequiredFields,
    basicRequiredFields,
    fullRequiredFields
  ]);

  // get carrier information and set user
  useEffect(() => {
    (async function() {
      const [carriers, u] = await Promise.all([
        getVendorConnectedCompanies(),
        getAccountInfo()
      ]);

      setUser(u);

      const c = carriers.filter(car => car.carrierId === u.requestingCompanyId);

      if (!c || c.length < 1) {
        setCarrier(carriers[0]);
      } else {
        setCarrier(c[0]);
      }
      setSkipRequiredDocs(c[0]?.uploadingDocsOptional);
    })();
  }, [carrier?.uploadingDocsOptional]);

  // handles scenario where user selects financed-only claims
  // then changes financing to non-financed (owned)
  useEffect(() => {
    if (financed) return;

    const currentOrders = selectedOrders;
    const currentFullOrders = allOrderTypes.filter(
      order => currentOrders?.[order.name]
    );
    const currentFinancedOrders = currentFullOrders.filter(
      order => order.financedOnly
    );

    // remove financed orders from the selected orders
    if (currentFinancedOrders.length > 0) {
      const n = currentFullOrders.filter(order => !order?.financedOnly);
      updateSelectedOrders(n);
    }
  }, [financed, selectedOrders]);

  // if there is no lender when packet is loaded, set lender
  useEffect(() => {
    if (
      packet?.lenderName &&
      packet?.lenderName !== "No Lender" &&
      !lender?.lenderName
    ) {
      setLender({ lenderName: packet.lenderName });
    }
  }, [packet, lender]);

  // sets stepper first step when mode changes
  useEffect(() => {
    stepperRef?.current?.setActiveStep(0);
  }, [mode]);

  const showToast = ({ severity, summary, detail }) => {
    toastRef.current.show({ severity, summary, detail, life: 5000 });
  };

  const updateBasicClaimInfo = async basicClaimInfoForm => {
    setBasicClaimInfo(basicClaimInfoForm);
    if (!packet) {
      const updateValue = JSON.parse(JSON.stringify(basicClaimInfoForm));
      if (lender?.lenderId) {
        updateValue.lenderId = lender.lenderId;
      } else if (!lender?.lenderName) {
        updateValue.lenderId = "f26b3307-77b1-420e-affc-a65fd6e0c73c"; // hardcoded no lender
      }
      if (lender?.lenderName) {
        if (lender?.lenderName.substring(0, 4) === "Add ") {
          updateValue.lenderName = lender?.lenderName.substring(4);
        } else {
          updateValue.lenderName = lender?.lenderName;
        }
      } else {
        updateValue.lenderName = "No Lender";
      }

      delete updateValue.lender;
      setPacket(await createPacket(updateValue));
    } else {
      const updateValue = JSON.parse(JSON.stringify(basicClaimInfoForm));
      if (lender?.lenderId) {
        updateValue.lenderId = lender.lenderId;
      } else if (!lender?.lenderName) {
        updateValue.lenderId = "f26b3307-77b1-420e-affc-a65fd6e0c73c"; // hardcoded no lender
      } else {
        updateValue.lenderId = null;
      }
      if (lender?.lenderName) {
        if (lender?.lenderName.substring(0, 4) === "Add ") {
          updateValue.lenderName = lender?.lenderName.substring(4);
        } else {
          updateValue.lenderName = lender?.lenderName;
        }
      } else {
        updateValue.lenderName = "No Lender";
      }
      const payload = {
        ...(updateValue?.accountNumber && {
          accountNumber: updateValue.accountNumber
        }),
        ...(updateValue?.carrierId && { carrierId: updateValue.carrierId }),
        claimNumber: updateValue.claimNumber,
        financeType: updateValue.financeType,
        ...(updateValue.lenderId && { lenderId: updateValue.lenderId }),
        ...(updateValue?.lenderName && { lenderName: updateValue.lenderName }),
        vin: updateValue.vin
      };
      // if any values changed OTHER THAN account number, create update task
      if (
        Object.keys(payload).some(
          key => packet[key] !== payload[key] && key !== "accountNumber"
        )
      ) {
        payload.createUpdateTask = true;
      } else {
        payload.createUpdateTask = false;
      }
      const p = await updatePacket(packet.packetId, payload);
      setPacket(p);
      refreshPacket();
    }
  };

  // restructures claim data from form, updates packet, and places orders
  const submitClaim = async (fullClaimInfoForm, extra, edit = false) => {
    setSubmitting(true);
    try {
      const b = basicClaimInfo;
      const f = fullClaimInfoForm;
      const fullTRA =
        f?.acquiringPartyMailingAddress || f?.titleRemittanceAddress;
      // const structuredTRA = {
      //   streetAddress: fullTRA?.streetAddress,
      //   ...(fullTRA?.streetAddress2 && {
      //     streetAddress2: fullTRA?.streetAddress2
      //   }),
      //   city: fullTRA?.city,
      //   stateId: fullTRA?.state?.stateId || fullTRA?.state,
      //   zipCode: fullTRA?.zipCode
      // };
      const isSalvage = salvageBrands.includes(extra?.requestedTitleBrand);
      const existingFormat = titleType?.value;
      const structuredClaimData = {
        odometer: f?.odometer,
        ownersName: f?.ownersName,
        ownersStreetAddress: f?.ownersAddress?.streetAddress,

        ...(f?.ownersAddress?.streetAddress2 && {
          ownersStreetAddress2: f?.ownersAddress?.streetAddress2
        }),
        ...(f?.ownersAddress?.city && { ownersCity: f?.ownersAddress?.city }),
        ...(f?.ownersAddress?.zipCode && {
          ownersZipCode: f?.ownersAddress?.zipCode
        }),

        ownersPhoneNumber: f?.ownersPhoneNumber,
        ...(f?.ownersEmailAddress && {
          ownersEmailAddress: f?.ownersEmailAddress
        }),
        ownersStateId: f?.ownersAddress?.state?.stateId,
        ...(f?.settlementAmount && {
          settlementAmount: parseFloat(f?.settlementAmount)
        }),
        ...(f?.deductible && { deductible: parseFloat(f?.deductible) }),
        dateOfLoss: f?.dateOfLoss,
        adjusterName: f?.adjusterInfo?.adjusterName || user.name,
        ...(f?.adjusterInfo?.adjusterPhoneNumber && {
          adjusterPhoneNumber: f?.adjusterInfo?.adjusterPhoneNumber
        }),
        ...(!f?.adjusterInfo.adjusterPhoneNumber &&
          user.phoneNumber && { adjusterPhoneNumber: user.phoneNumber }),
        adjusterEmailAddress:
          f?.adjusterInfo?.adjusterEmailAddress || user.email,
        financeType: b?.financeType,

        ...(typeof fullTRA === "string" && {
          titleRemittanceAddress: f?.titleRemittanceAddress
        }),
        ...(typeof fullTRA === "object" && {
          titleRemittanceAddress: buildAddressStringFromObject(fullTRA, false)
        }),

        // if no TRA is provided and xTitle is not selected and there is exactly 1 default address
        // grab that and use it as the TRA
        ...(!f?.titleRemittanceAddress &&
          !xTitleSelected &&
          carrier?.titleRemittanceAddresses?.length === 1 && {
            titleRemittanceAddress: buildAddressStringFromObject(
              carrier.titleRemittanceAddresses[0],
              false
            )
          }),

        ...((f?.titleRemittanceAddressId ||
          extra?.titleRemittanceAddressId) && {
          titleRemittanceAddressId:
            f?.titleRemittanceAddressId || extra?.titleRemittanceAddressId
        }),

        ...(f?.vehicleLocation && {
          vehicleLocation: f?.vehicleLocation
        }),
        ownerRetained: f?.ownerRetained || false,
        causeOfLoss: f?.causeOfLoss,
        insurerType: f?.thirdPartyClaim ? "Third Party" : "First Party",
        vehicle: {
          ...(f?.vehicle?.make && { make: f?.vehicle.make }),
          ...(f?.vehicle?.model && { model: f?.vehicle.model }),
          ...(f?.vehicle?.year && { year: f?.vehicle.year }),
          ...(f?.vehicle?.trim && { vehicleTrim: f?.vehicle.trim }),
          ...(f?.vehicle?.vehicleType && {
            vehicleType: f?.vehicle.vehicleType
          })
        },

        titledState: titleState?.stateInitials,

        ...(xTitleSelected && {
          titleTransfer: {
            damages: extra?.damages,
            ...(!f?.odometerExempt && {
              odometerReadDate: f?.odometerReadingDate
            }),
            titleTransferDate: extra?.titleTransferDate || new Date(),
            titleNumber: f?.titleNumber,
            existingFormat,
            requestedFormat: extra?.requestedTitleType.toUpperCase(),
            odometerExempt: f?.odometerExempt ?? false,
            ...(f?.odometerExempt &&
              f?.odometerExemptReason && {
                odometerExemptReason: f?.odometerExemptReason
              }),
            representative: {
              name: f?.adjusterInfo.adjusterName,
              emailAddress: f?.adjusterInfo?.adjusterEmailAddress,
              title: "Adjuster"
            },
            isSalvage,
            requestedBrand: extra?.requestedTitleBrand,
            ...(isSalvage && { damages: extra?.damages }),
            ...(f?.acquiringPartyMailingAddress && {
              acquiringPartyMailingAddress: {
                streetAddress: f?.acquiringPartyMailingAddress?.streetAddress,
                ...(f?.acquiringPartyMailingAddress?.streetAddress2 && {
                  streetAddress2:
                    f?.acquiringPartyMailingAddress?.streetAddress2
                }),
                city: f?.acquiringPartyMailingAddress?.city,
                state:
                  f?.acquiringPartyMailingAddress?.state?.stateInitials ||
                  f?.acquiringPartyMailingAddress?.state,
                zipCode: f?.acquiringPartyMailingAddress?.zipCode
              }
            }),
            disposingPartyName: f?.disposingPartyName,
            disposingPartyAddress: {
              streetAddress: f?.disposingPartyAddress?.streetAddress,
              ...(f?.disposingPartyAddress?.streetAddress2 && {
                streetAddress2: f?.disposingPartyAddress?.streetAddress2
              }),
              city: f?.disposingPartyAddress?.city,
              state: f?.disposingPartyAddress?.state?.stateInitials,
              zipCode: f?.disposingPartyAddress?.zipCode
            }
          }
        }),
        titledOwners: [
          {
            type: f?.lenderHoldsTitle ? "company" : "person",
            name: f?.disposingPartyName || f?.ownersName,
            phoneNumber: f?.disposingPartyPhoneNumber || f?.ownersPhoneNumber,
            streetAddress:
              f?.disposingPartyAddress?.streetAddress ||
              f?.ownersAddress?.streetAddress,
            ...((f?.disposingPartyAddress?.streetAddress2 ||
              f?.ownersAddress?.streetAddress2) && {
              streetAddress2:
                f?.disposingPartyAddress?.streetAddress2 ||
                f?.ownersAddress?.streetAddress2
            }),
            city: f?.disposingPartyAddress?.city || f?.ownersAddress?.city,
            state:
              f?.disposingPartyAddress?.state?.stateInitials ||
              f?.ownersAddress?.state?.stateInitials,
            zipCode:
              f?.disposingPartyAddress?.zipCode || f?.ownersAddress?.zipCode
          }
        ],
        createUpdateTask: edit
      };
      // if not xTitle, store titleRemittanceAddress object in titleTransfer.acquiringPartyPhysicalAddress & acquiringPartyMailingAddress
      if (
        !xTitleSelected &&
        typeof fullTRA === "object" &&
        !Object.entries(fullTRA)
          .filter(([key]) => key !== "streetAddress2")
          .some(([, value]) => value == null) &&
        fullTRA.state !== "WV"
      ) {
        const state =
          typeof fullTRA.state === "object"
            ? fullTRA.state.stateName
            : states?.find(s => s.stateInitials === fullTRA.state)?.stateName;
        const structuredTRA = {
          streetAddress: fullTRA?.streetAddress,
          ...(fullTRA?.streetAddress2 && {
            streetAddress2: fullTRA?.streetAddress2
          }),
          city: fullTRA?.city,
          state,
          zipCode: fullTRA?.zipCode
        };
        if (Object.keys(structuredTRA).length > 0) {
          structuredClaimData.titleTransfer = {
            acquiringPartyMailingAddress: { ...structuredTRA }
          };
        }
      }
      await updatePacket(packet.packetId, structuredClaimData);
      await placeOrders();
      setFullClaimInfo(fullClaimInfoForm);
      setSubmitting(false);
      stepperRef?.current?.setActiveStep(0);
    } catch (ex) {
      showToast({
        severity: "error",
        summary: "Error",
        detail: "There was an error submitting the claim"
      });
      setSubmitting(false);
    }
  };

  const placeOrders = async o => {
    if (!submitting) {
      setSubmitting(true);
    }
    const orders = o || selectedOrders;
    const promises = [];
    // if orders contains everything in group, hit new route to handle the group, then continue as normal
    if (
      carrier?.orderGrouping &&
      orders["letterOfGuarantee"] &&
      !existingOrders["letterOfGuarantee"]
    ) {
      promises.push(createOrderGroupRequest(packet.packetId));
    } else {
      if (orders["loanPayoff"]) {
        promises.push(createPayoffQuote(packet.packetId));
        delete orders["loanPayoff"];
      }
      if (orders["letterOfGuarantee"]) {
        promises.push(createLoGRequest(packet.packetId, "Letter of Guarantee"));
        delete orders["letterOfGuarantee"];
      }
      if (Object.keys(orders).length > 0) {
        const orderLabels = Object.keys(orders).map(order => {
          if (order === "copyOfTitle") {
            return "Copy of Title";
          }
          if (order === "titleTransfer") {
            return "Title Transfer";
          }
          return allOrderTypes.find(o => o.name === order).label;
        });
        promises.push(createOrderRequest(packet.packetId, orderLabels));
      }
    }
    await Promise.all(promises);
    setXTitleChecked(false);
    setSubmitting(false);

    const p = await fetchPacket(packet.packetId);
    setPacket(p);
    refreshPacket();
    setSelectedOrders([]);
    changeMode("orders");
  };

  const updateSelectedOrders = selectedOrdersForm => {
    const titleState = selectedOrdersForm.titleState;
    const titleType = selectedOrdersForm.titleType;
    delete selectedOrdersForm.titleState;
    delete selectedOrdersForm.titleType;
    const filteredOrders = Object.fromEntries(
      Object.entries(selectedOrdersForm).filter(([_, value]) => value)
    );

    if (filteredOrders.length === 0) {
      setOptionalFields([]);
      setSelectedOrders([]);
      setFullRequiredFields([]);
      return;
    }
    setSelectedOrders(filteredOrders);
    setTitleTypes({ titleState, titleType });
  };

  return (
    <SidebarContext.Provider
      value={{
        ownerSignedTitleImage,
        setOwnerSignedTitleImage,
        odometerInTitleImage,
        setOdometerInTitleImage,
        signedLRInTitleImage,
        setSignedLRInTitleImage
      }}
    >
      <div className={"Content"}>
        <Toast
          ref={toastRef}
          position={"top-right"}
          baseZIndex={1000000000}
        ></Toast>
        {testing && <div>Mode: {mode}</div>}
        <Stepper
          ref={stepperRef}
          orientation={"vertical"}
          linear={!workingPacket}
        >
          {mode === "create" && (
            <StepperPanel
              header={
                existingPacket
                  ? "Update Basic Claim Information"
                  : "Basic Claim Information"
              }
            >
              <BasicClaimInfoForm
                basicClaimInfo={basicClaimInfo}
                basicClaimInfoSubmit={updateBasicClaimInfo}
                requiredFields={basicRequiredFields}
                stepperRef={stepperRef}
                existingPacket={existingPacket}
                carrier={carrier}
                user={user}
                lender={lender}
                setLender={setLender}
                financed={financed}
                setFinanced={setFinanced}
                mode={mode}
                changeMode={changeMode}
                testing={testing}
              />
            </StepperPanel>
          )}

          {(mode === "create" || mode === "orders" || mode === "newxTitle") && (
            <StepperPanel
              header={existingPacket ? "Select New Orders" : "Select Orders"}
              disabled={true}
            >
              <OrderSelectionList
                carrier={carrier}
                basicClaimInfo={basicClaimInfo}
                selectedOrders={selectedOrders}
                setSelectedOrders={setSelectedOrders}
                updateSelectedOrders={updateSelectedOrders}
                currentOrders={existingOrders}
                // add type to payoff to make order logic easier within OrderSelectionList
                packetOrders={
                  packet?.orders && [
                    ...packet?.orders,
                    packet?.payoffQuote && {
                      ...packet.payoffQuote,
                      type: "Loan Payoff"
                    }
                  ]
                }
                xTitleSelected={xTitleSelected}
                setXTitleSelected={setXTitleSelected}
                xTitleChecked={xTitleChecked}
                setXTitleChecked={setXTitleChecked}
                financed={financed}
                completedOrderTypes={completedOrderTypes}
                setCompletedOrderTypes={setCompletedOrderTypes}
                titleTypes={titleTypes}
                stepperRef={stepperRef}
                titleState={titleState}
                setTitleState={setTitleState}
                titleType={titleType}
                setTitleType={setTitleType}
                mode={mode}
                changeMode={changeMode}
                revertMode={revertMode}
              />
            </StepperPanel>
          )}

          {(mode === "create" ||
            mode === "documents" ||
            mode === "orders" ||
            mode === "newxTitle") && (
            <StepperPanel
              header={
                existingPacket
                  ? "Upload Additional Documents"
                  : "Document Uploads"
              }
            >
              <DocumentUploadPage
                packet={packet}
                selectedOrders={selectedOrders}
                existingOrders={existingOrders}
                skipRequiredDocs={skipRequiredDocs}
                titleType={titleType}
                xTitleSelected={xTitleSelected}
                stepperRef={stepperRef}
                uploadedDocs={uploadedDocs}
                setUploadedDocs={setUploadedDocs}
                mode={mode}
                revertMode={revertMode}
                testing={testing}
              ></DocumentUploadPage>
            </StepperPanel>
          )}

          {(mode === "create" || mode === "newxTitle" || mode === "orders") && (
            <StepperPanel
              header={
                existingPacket
                  ? "Update Detailed Information"
                  : "Detailed Information"
              }
            >
              <FullClaimInfoForm
                stepperRef={stepperRef}
                packet={packet}
                xTitleSelected={xTitleSelected}
                existingPacket={existingPacket}
                selectedOrders={selectedOrders}
                existingOrders={existingOrders}
                fullClaimInfo={fullClaimInfo}
                setFullClaimInfo={setFullClaimInfo}
                submitClaim={submitClaim}
                requiredFields={fullRequiredFields}
                optionalFields={optionalFields}
                carrier={carrier}
                submitting={submitting}
                states={states}
                mode={mode}
                testing={testing}
              ></FullClaimInfoForm>
            </StepperPanel>
          )}
          {mode === "edit" && (
            <StepperPanel header={"Edit Basic Information"}>
              <BasicClaimInfoForm
                basicClaimInfo={basicClaimInfo}
                basicClaimInfoSubmit={updateBasicClaimInfo}
                requiredFields={basicRequiredFields}
                stepperRef={stepperRef}
                existingPacket={existingPacket}
                carrier={carrier}
                lender={lender}
                setLender={setLender}
                mode={mode}
                changeMode={changeMode}
              />
            </StepperPanel>
          )}
          {mode === "edit" && (
            <StepperPanel header="Edit Claim Information">
              <FullClaimInfoForm
                stepperRef={stepperRef}
                packet={packet}
                xTitleSelected={xTitleSelected}
                existingPacket={existingPacket}
                selectedOrders={selectedOrders}
                existingOrders={existingOrders}
                basicClaimInfo={basicClaimInfo}
                fullClaimInfo={fullClaimInfo}
                setFullClaimInfo={setFullClaimInfo}
                submitClaim={submitClaim}
                requiredFields={fullRequiredFields}
                optionalFields={optionalFields}
                carrier={carrier}
                submitting={submitting}
                states={states}
                mode={mode}
                testing={testing}
              ></FullClaimInfoForm>
            </StepperPanel>
          )}
        </Stepper>
      </div>
    </SidebarContext.Provider>
  );
};

export default CreateNewClaimModal;
